// external
import { Tooltip } from "react-tooltip";

// components
import CustomButton from "../../../components/partials/custom-button/custom-button";

// assets
import Icons from "assets/icons";
import MODEL_BUILD_STATUS from "../constants/model-build-status";

// api
import apiRoot from "../../../api/apiRoot";

const formatDate = (date) => {
  return date ? new Date(parseInt(date)).toISOString().split("T")[0] : "";
};

export const formatFloats = (value, style = "") => {
  // Return null for any falsy values or values that would result in NaN
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    isNaN(parseFloat(value))
  ) {
    return value;
  }

  let options = {};
  if (style === "currency") {
    options = {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    };
  }
  if (style === "percent") {
    options = {
      style: "percent",
      minimumFractionDigits: 2,
    };
  }
  return parseFloat(value).toLocaleString("en-US", options);
};

// formatters for different model values
// handles different styling for properties as well as default text and date formatting
export const FORMATTERS = {
  Author: (model) => ({
    content: model.authorName || "Unknown",
    styling: "",
  }),
  "Model name": (model) => ({
    content: model.versionName || "Unknown",
    styling: "",
  }),
  "Last updated": (model) => ({
    content: formatDate(model?.lastUpdatedDate) || "Unknown",
    styling: "",
  }),
  "Aid results": (model) => ({
    content: model.aidResults?.generated ? "Generated" : "Not generated",
    styling: model.aidResults?.generated
      ? "font-medium rounded-md bg-green-02 text-green-01 px-2"
      : "font-medium rounded-md bg-gray-06 text-gray-03 px-2",
  }),
  "Est. Enrollment": (model) => ({
    content: formatFloats(model.aidResults?.estEnrollment) || "Unknown",
    styling: "",
  }),
  "Est. NTR": (model) => ({
    content:
      formatFloats(model.aidResults?.estNetRevenue, "currency") || "Unknown",
    styling: "",
  }),
  "Est. Inst. Aid": (model) => ({
    content:
      formatFloats(model.aidResults?.estInstitutionalAid, "currency") ||
      "Unknown",
    styling: "",
  }),
  "Est. Discount Rate": (model) => ({
    content:
      formatFloats(model.aidResults?.estDiscountRate, "percent") || "Unknown",
    styling: "",
  }),
};

// if no formatter is found, it will return an object with the styling and content set to "Unknown"
export const formatModelValues = (value, model) => {
  const formatter = FORMATTERS[value];
  if (!formatter) {
    console.warn(`No formatter found for ${value}`);
    return { styling: "", content: "Unknown" };
  }
  return formatter(model);
};

// build button variants
export const BUILD_BTN_VARIANTS = {
  building: {
    btnLabel: "Building Aid Results...",
    icon: (
      <Icons.Spinner customClass="h-full w-4 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-900" />
    ),
    variant: "secondary",
  },
  notGenerated: {
    btnLabel: "Build Aid Results",
    icon: <Icons.Bolt aria-label="Build" customClass="h-full w-4" />,
    variant: "primary",
  },
};

export const unpublishModel = async (model) => {
  try {
    // flip the status to published for the current model
    const updatedModel = {
      ...model,
      aidResults: null,
      status: "draft",
    };
    await apiRoot.post(`/api/fao-model/unpublish/${model.id}`, updatedModel);
  } catch (error) {
    console.log(error);
    throw error; // Re-throw to allow caller to handle
  }
};

/**
 * Unified unpublish handler with loading states and navigation
 * Can be used across different components/contexts
 *
 * @param {Object} model - Model to unpublish
 * @param {Function} navigate - Navigation function from useNavigate
 * @param {Function} setLoading - Optional loading state setter
 * @param {Object} options - Optional configuration
 * @param {string} options.successRoute - Route to navigate on success (default: "../fao-builder/fao-view")
 * @param {string} options.errorRoute - Route to navigate on error (default: "../fao-builder/fao-edit")
 */
export const handleUnpublishModel = async (
  model,
  navigate,
  setLoading = null,
  options = {}
) => {
  const {
    successRoute = "../fao-builder/fao-view",
    errorRoute = "../fao-builder/fao-edit",
  } = options;

  // Set loading state if provided
  if (setLoading) {
    setLoading((prev) => ({ ...prev, model: true }));
  }

  try {
    await unpublishModel(model);
    navigate(successRoute);
  } catch (error) {
    console.log(error);
    navigate(errorRoute);
  } finally {
    // Clear loading state if provided
    if (setLoading) {
      setLoading((prev) => ({ ...prev, model: false }));
    }
  }
};

export const renderTooltip = (generated) => {
  if (generated) {
    return (
      <Tooltip anchorSelect="#clickable1" clickable>
        <CustomButton>View Aid Results</CustomButton>
      </Tooltip>
    );
  }
  return (
    <Tooltip anchorSelect="#clickable2" clickable>
      <CustomButton>No Aid Results built yet</CustomButton>
    </Tooltip>
  );
};
